import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosConfig";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";

const BATCH_SIZES = [25, 50, 100, 200, 500];
const STATUSES = [
  "DRAFT",
  "QUEUED",
  "SENDING",
  "PAUSED",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
];
const RECIPIENT_STATUSES = [
  "QUEUED",
  "SENDING",
  "SENT",
  "DELIVERED",
  "BOUNCED",
  "COMPLAINED",
  "SKIPPED",
  "FAILED",
];

const blankCampaign = {
  name: "",
  subject: "",
  preview_text: "",
  html_content: "",
  marketing_segment: "",
};

const errorMessage = (error) => {
  const data = error?.response?.data;
  const details = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
  return details || data?.message || error?.message || "Request failed";
};

const percentage = (value, total) =>
  total > 0 ? `${((Number(value || 0) / total) * 100).toFixed(1)}%` : "0.0%";

export default function MarketingCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankCampaign);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [segmentDialog, setSegmentDialog] = useState(null);
  const [segmentId, setSegmentId] = useState("");
  const [segmentResult, setSegmentResult] = useState(null);
  const [tags, setTags] = useState([]);
  const [batchSize, setBatchSize] = useState(200);
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientPages, setRecipientPages] = useState(1);
  const [recipientStatus, setRecipientStatus] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [testProfiles, setTestProfiles] = useState([]);
  const [profileQuery, setProfileQuery] = useState("");
  const [profileOptions, setProfileOptions] = useState([]);
  const [tagTest, setTagTest] = useState(null);

  const request = async (promise, successMessage) => {
    setBusy(true);
    try {
      const response = await promise;
      if (successMessage)
        toast.success(response.data?.message || successMessage);
      return response.data;
    } catch (error) {
      const message = errorMessage(error);
      toast.error(message);
      if (error?.response?.data) {
        const payload = error.response.data;
        if (payload.results) setTagTest(payload);
        else if (payload.data && payload.count !== undefined)
          setSegmentResult(payload);
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const loadCampaigns = useCallback(
    async (requestedPage = page, requestedStatus = statusFilter) => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          "/marketing/fetch-marketing-campaigns",
          {
            params: {
              page: requestedPage,
              limit: 50,
              ...(requestedStatus ? { status: requestedStatus } : {}),
            },
          },
        );
        setCampaigns(data.campaigns || []);
        setPage(data.page || requestedPage);
        setTotalPages(data.totalPages || 1);
      } catch (error) {
        toast.error(errorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [page, statusFilter],
  );

  const loadSegments = useCallback(async () => {
    try {
      const { data } = await axios.get("/marketing/fetch-marketing-segments");
      setSegments(data.data || []);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }, []);

  useEffect(() => {
    loadCampaigns(1, statusFilter);
  }, [statusFilter, loadCampaigns]);
  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  const updateField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const openCreate = () => {
    setEditing(null);
    setForm(blankCampaign);
    setEditorOpen(true);
  };

  const openEdit = (campaign) => {
    setEditing(campaign);
    setForm({
      name: campaign.name || "",
      subject: campaign.subject || "",
      preview_text: campaign.preview_text || "",
      html_content: campaign.html_content || "",
      marketing_segment:
        campaign.marketing_segment ?? campaign.marketing_segment_id ?? "",
    });
    setEditorOpen(true);
  };

  const saveCampaign = async () => {
    if (
      !form.name.trim() ||
      !form.subject.trim() ||
      !form.html_content.trim()
    ) {
      toast.error("Campaign name, subject and HTML content are required.");
      return;
    }
    if (!/<[a-z][\s\S]*>/i.test(form.html_content)) {
      toast.error("Enter valid HTML content.");
      return;
    }
    if (!/{{\s*[a-zA-Z][a-zA-Z0-9_.]*\s*}}/.test(form.html_content)) {
      toast.error("Add at least one personalization tag, e.g. {{name}}.");
      return;
    }
    const payload = {
      ...form,
      marketing_segment:
        form.marketing_segment === "" ? null : Number(form.marketing_segment),
    };
    const result = await request(
      editing
        ? axios.put(`/marketing/marketing-campaign/${editing.id}`, payload)
        : axios.post("/marketing/create-campaign", payload),
      editing ? "Campaign updated" : "Campaign created",
    );
    if (result) {
      setEditorOpen(false);
      await loadCampaigns();
    }
  };

  const getTags = async (campaign) => {
    const result = await request(
      axios.post(`/marketing/fetch-campaign-tags/${campaign.id}/`),
    );
    if (result) {
      setSelectedCampaign(campaign);
      setTags(result.tags || []);
      setTagTest({
        message: result.message,
        count: result.count,
        tags: result.tags || [],
      });
      setPreviewOpen(true);
    }
  };

  const openQueue = async (campaign) => {
    setSelectedCampaign(campaign);
    setSegmentId(String(campaign.marketing_segment ?? ""));
    setSegmentResult(null);
    await loadSegments();
    setSegmentDialog("queue");
  };

  const inspectSegment = async () => {
    if (!segmentId) return toast.error("Select a segment first.");
    const result = await request(
      axios.get(`/marketing/test-marketing-segment/${segmentId}`),
    );
    if (result) setSegmentResult(result);
  };

  const queueCampaign = async () => {
    if (!segmentId) return toast.error("Select a segment first.");
    const result = await request(
      axios.post(`/marketing/queue-campaign/${selectedCampaign.id}`),
      "Campaign queued",
    );
    if (result) {
      setSegmentDialog(null);
      await loadCampaigns();
    }
  };

  const runProcess = async (campaign) => {
    const confirmed = window.confirm(
      `Process up to ${batchSize} queued recipients for "${campaign.name}"? This sends real emails.`,
    );
    if (!confirmed) return;
    const result = await request(
      axios.post(`/marketing/process-campaign/${campaign.id}/${batchSize}`),
      "Batch processed",
    );
    if (result) await loadCampaigns();
  };

  const campaignAction = async (campaign, action, success) => {
    const result = await request(
      axios.post(`/marketing/${action}/${campaign.id}`),
      success,
    );
    if (result) await loadCampaigns();
  };

  const openReport = async (campaign) => {
    const result = await request(
      axios.get(`/marketing/fetch-campaign-report/${campaign.id}`),
    );
    if (result) {
      setReport(result);
      setSelectedCampaign(campaign);
      setReportOpen(true);
    }
  };

  const openRecipients = async (
    campaign,
    requestedPage = 1,
    status = recipientStatus,
    email = recipientEmail,
  ) => {
    setSelectedCampaign(campaign);
    setRecipientPage(requestedPage);
    setRecipientStatus(status);
    const result = await request(
      axios.get(`/marketing/fetch-campaign-recipients/${campaign.id}`, {
        params: {
          page: requestedPage,
          limit: 50,
          ...(status ? { status } : {}),
          ...(email?.trim() ? { email: email.trim() } : {}),
        },
      }),
    );
    if (result) {
      setRecipients(result.recipients || []);
      setRecipientPages(result.pagination?.total_pages || 1);
      setRecipientsOpen(true);
    }
  };

  const searchProfiles = async (value) => {
    if (value.trim().length < 2) return setProfileOptions([]);
    try {
      const { data } = await axios.get("/compare/get-profiles", {
        params: { query: value },
      });
      setProfileOptions(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      searchProfiles(e.target.value);
    }
  };

  const addProfile = (profile) => {
    if (!testProfiles.some((item) => item.id === profile.id)) {
      setTestProfiles((current) => [...current, profile]);
    }
    setProfileQuery("");
    setProfileOptions([]);
  };

  const sendTest = async () => {
    if (!selectedCampaign) return;
    if (!testProfiles.length)
      return toast.error("Select at least one profile.");
    const result = await request(
      axios.post(`/marketing/test-campaign/${selectedCampaign.id}`, {
        profile_ids: testProfiles.map((profile) => profile.id),
      }),
      "Test campaign completed",
    );
    if (result) {
      setTagTest(result);
      setTestProfiles([]);
    }
  };

  const retryFailed = async (campaign) => {
    const result = await request(
      axios.post(`/marketing/retry-failed-recipients/${campaign.id}`),
      "Failed recipients queued for retry",
    );
    if (result) await loadCampaigns();
  };

  const statusColor = (status) =>
    ({
      DRAFT: "default",
      QUEUED: "info",
      SENDING: "warning",
      PAUSED: "warning",
      COMPLETED: "success",
      FAILED: "error",
      CANCELLED: "error",
    })[status] || "default";

  const totalForReport = useMemo(
    () =>
      Number(
        report?.statistics?.find((item) => item.name === "ALL")?.value || 0,
      ),
    [report],
  );

  return (
    <div style={{ padding: 24, maxWidth: 1500, margin: "0 auto" }}>
      <ToastContainer position="top-right" />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <Typography variant="h4" fontWeight={700}>
            Marketing Campaigns
          </Typography>
          <Typography color="text.secondary">
            Create, test, send and monitor marketing emails.
          </Typography>
        </div>
        <Button variant="contained" onClick={openCreate}>
          Create campaign
        </Button>
      </div>

      <div
        style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}
      >
        <TextField
          select
          size="small"
          label="Filter by status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Batch size"
          value={batchSize}
          onChange={(e) => setBatchSize(Number(e.target.value))}
          style={{ minWidth: 150 }}
        >
          {BATCH_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size} recipients
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => loadCampaigns(page)}>
          Refresh
        </Button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <CircularProgress />
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent>No campaigns found.</CardContent>
        </Card>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {campaigns.map((campaign) => (
            <Card key={campaign.id} variant="outlined">
              <CardContent>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    alignItems: "start",
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {campaign.name}
                  </Typography>
                  <Chip
                    size="small"
                    color={statusColor(campaign.status)}
                    label={campaign.status}
                  />
                </div>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mt: 1, mb: 2 }}
                >
                  {campaign.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created:{" "}
                  {campaign.created_at
                    ? new Date(campaign.created_at).toLocaleString()
                    : "—"}{" "}
                  (
                  {segments.find((r) => r.id === campaign.marketing_segment)
                    ?.name ?? "No segment"}
                  )
                </Typography>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 16,
                  }}
                >
                  {campaign.status === "DRAFT" && (
                    <>
                      <Button size="small" onClick={() => openEdit(campaign)}>
                        Edit
                      </Button>
                      <Button size="small" onClick={() => openQueue(campaign)}>
                        Queue
                      </Button>
                    </>
                  )}
                  {["QUEUED", "SENDING"].includes(campaign.status) && (
                    <>
                      <Button size="small" onClick={() => runProcess(campaign)}>
                        Process batch
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          campaignAction(
                            campaign,
                            "pause-campaign",
                            "Campaign paused",
                          )
                        }
                      >
                        Pause
                      </Button>
                    </>
                  )}
                  {campaign.status === "PAUSED" && (
                    <Button
                      size="small"
                      onClick={() =>
                        campaignAction(
                          campaign,
                          "resume-campaign",
                          "Campaign resumed",
                        )
                      }
                    >
                      Resume
                    </Button>
                  )}
                  {[
                    "COMPLETED",
                    "FAILED",
                    "SENDING",
                    "QUEUED",
                    "PAUSED",
                  ].includes(campaign.status) && (
                    <Button size="small" onClick={() => openReport(campaign)}>
                      Analytics
                    </Button>
                  )}
                  {["COMPLETED", "FAILED", "SENDING"].includes(
                    campaign.status,
                  ) && (
                    <Button size="small" onClick={() => retryFailed(campaign)}>
                      Retry failed
                    </Button>
                  )}
                  {[
                    "QUEUED",
                    "SENDING",
                    "PAUSED",
                    "COMPLETED",
                    "FAILED",
                  ].includes(campaign.status) && (
                    <Button
                      size="small"
                      onClick={() => openRecipients(campaign, 1, "")}
                    >
                      Recipients
                    </Button>
                  )}
                  {!["COMPLETED", "CANCELLED"].includes(campaign.status) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() =>
                        window.confirm("Cancel this campaign?") &&
                        campaignAction(
                          campaign,
                          "cancel-marketing-campaign",
                          "Campaign cancelled",
                        )
                      }
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCampaign(campaign);
                      setTestProfiles([]);
                      setTagTest(null);
                      setSegmentDialog("test");
                    }}
                  >
                    Test campaign
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCampaign(campaign);
                      setForm({
                        name: campaign.name || "",
                        subject: campaign.subject || "",
                        preview_text: campaign.preview_text || "",
                        html_content: campaign.html_content || "",
                        marketing_segment: campaign.marketing_segment ?? "",
                      });
                      getTags(campaign);
                    }}
                  >
                    Preview HTML
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 20,
        }}
      >
        <Button disabled={page <= 1} onClick={() => loadCampaigns(page - 1)}>
          Previous
        </Button>
        <Typography>
          Page {page} of {totalPages}
        </Typography>
        <Button
          disabled={page >= totalPages}
          onClick={() => loadCampaigns(page + 1)}
        >
          Next
        </Button>
      </div>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {editing ? "Edit campaign" : "Create campaign"}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 14,
              paddingTop: 8,
            }}
          >
            <TextField
              label="Campaign name"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              required
            />
            <TextField
              label="Email subject"
              value={form.subject}
              onChange={(e) => updateField("subject", e.target.value)}
              required
            />
            <TextField
              label="Preview text"
              value={form.preview_text}
              onChange={(e) => updateField("preview_text", e.target.value)}
            />
            <TextField
              select
              label="Default segment (optional)"
              value={form.marketing_segment}
              onChange={(e) => updateField("marketing_segment", e.target.value)}
            >
              <MenuItem value="">No segment selected</MenuItem>
              {segments.map((segment) => (
                <MenuItem key={segment.id} value={segment.id}>
                  {segment.name || segment.key || `Segment ${segment.id}`}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <TextField
            label="HTML email content"
            value={form.html_content}
            onChange={(e) => updateField("html_content", e.target.value)}
            multiline
            minRows={14}
            fullWidth
            sx={{ mt: 2 }}
            helperText={
              'Personalization tags use {{tag_name}}, for example {{name}}. Use "View tags" / test recipients to validate required profile fields.'
            }
          />
          <div
            style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedCampaign({ ...editing, ...form });
                setPreviewOpen(true);
              }}
            >
              Preview HTML
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const found = [
                  ...form.html_content.matchAll(
                    /{{\s*([a-zA-Z][a-zA-Z0-9_.]*)\s*}}/g,
                  ),
                ].map((m) => m[1]);
                setTags([...new Set(found)]);
                setTagTest({
                  message: "Tags detected in HTML",
                  count: new Set(found).size,
                  tags: [...new Set(found)],
                });
              }}
            >
              Extract tags locally
            </Button>
          </div>
          {tags.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Tags: {tags.map((tag) => `{{${tag}}}`).join(", ")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Close</Button>
          <Button variant="contained" disabled={busy} onClick={saveCampaign}>
            Save draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={segmentDialog === "queue"}
        onClose={() => setSegmentDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Choose campaign audience</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            See the segment that should receive this campaign. Review the
            audience count before queueing.
          </Typography>
          <TextField
            select
            fullWidth
            label="Marketing segment"
            value={segmentId}
            onChange={(e) => {
              setSegmentId(e.target.value);
              setSegmentResult(null);
            }}
            disabled={true}
          >
            {segments.map((segment) => (
              <MenuItem key={segment.id} value={segment.id}>
                {segment.name || segment.key || `Segment ${segment.id}`}
              </MenuItem>
            ))}
          </TextField>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={inspectSegment}
            disabled={!segmentId || busy}
          >
            Preview audience
          </Button>
          {segmentResult && (
            <div style={{ marginTop: 16 }}>
              <Typography fontWeight={700}>
                {segmentResult.count} eligible profiles found
              </Typography>
              <div style={{ maxHeight: 180, overflow: "auto", marginTop: 8 }}>
                {(segmentResult.data || []).slice(0, 30).map((profile) => (
                  <Typography variant="body2" key={profile.id}>
                    {profile.email}
                  </Typography>
                ))}
              </div>
              {segmentResult.count > 30 && (
                <Typography variant="caption">Showing first 30.</Typography>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSegmentDialog(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={!segmentId || busy}
            onClick={queueCampaign}
          >
            Queue campaign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={segmentDialog === "test"}
        onClose={() => setSegmentDialog(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Test campaign: {selectedCampaign?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select profiles to receive a test email. This does not queue the
            campaign or change its status.
          </Typography>
          <TextField
            fullWidth
            label="Search profiles by name or email"
            value={profileQuery}
            onChange={(e) => setProfileQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {profileOptions.length > 0 && (
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 6,
                maxHeight: 180,
                overflow: "auto",
                marginTop: 4,
              }}
            >
              {profileOptions.map((profile) => (
                <div
                  key={profile.id}
                  onClick={() => addProfile(profile)}
                  style={{
                    padding: 10,
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {profile.name || "Unnamed"} — {profile.email}
                </div>
              ))}
            </div>
          )}
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}
          >
            {testProfiles.map((profile) => (
              <Chip
                key={profile.id}
                label={`${profile.name || profile.email} (${profile.email})`}
                onDelete={() =>
                  setTestProfiles((items) =>
                    items.filter((item) => item.id !== profile.id),
                  )
                }
              />
            ))}
          </div>
          {tagTest?.results && (
            <div style={{ marginTop: 16 }}>
              <Typography fontWeight={700}>{tagTest.message}</Typography>
              <Typography>
                Processed: {tagTest.processed} · Sent: {tagTest.sent} · Failed:{" "}
                {tagTest.failed}
              </Typography>
              {tagTest.results.map((result, index) => (
                <Typography key={`${result.email}-${index}`} variant="body2">
                  {result.email}: {result.status}
                  {result.message ? ` — ${result.message}` : ""}
                </Typography>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSegmentDialog(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={busy || !testProfiles.length}
            onClick={sendTest}
          >
            Send test emails
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {tags.length ? "Campaign tags" : "HTML preview"} —{" "}
          {selectedCampaign?.name}
        </DialogTitle>
        <DialogContent>
          {tagTest?.tags && (
            <Typography sx={{ mb: 2 }}>
              Detected tags:{" "}
              {tagTest.tags.length
                ? tagTest.tags.map((tag) => `{{${tag}}}`).join(", ")
                : "No personalization tags found."}
            </Typography>
          )}
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: 6,
              minHeight: 240,
              padding: 12,
            }}
          >
            <iframe
              title="Email HTML preview"
              sandbox=""
              srcDoc={
                selectedCampaign?.html_content ||
                form.html_content ||
                "<p>No HTML content</p>"
              }
              style={{ width: "100%", minHeight: 420, border: 0 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Campaign analytics — {report?.campaign?.name}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Percentages are calculated against all tracked campaign recipients (
            {totalForReport}).
          </Typography>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
              gap: 12,
            }}
          >
            {(report?.statistics || []).map((stat) => (
              <Card key={stat.name} variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    {stat.name}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {Number(stat.value || 0).toLocaleString()}
                  </Typography>
                  {stat.name !== "ALL" && (
                    <Typography variant="body2" color="text.secondary">
                      {percentage(stat.value, totalForReport)} of recipients
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <Button
            sx={{ mt: 2 }}
            onClick={() => {
              setReportOpen(false);
              openRecipients(selectedCampaign, 1, "");
            }}
          >
            View recipients
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recipientsOpen}
        onClose={() => setRecipientsOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Recipients — {selectedCampaign?.name}</DialogTitle>
        <DialogContent>
          <TextField
            select
            size="small"
            label="Recipient status"
            value={recipientStatus}
            onChange={(e) =>
              openRecipients(selectedCampaign, 1, e.target.value)
            }
            sx={{ mb: 2, minWidth: 220 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {RECIPIENT_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search email"
            placeholder="Enter email and press Enter"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                openRecipients(
                  selectedCampaign,
                  1,
                  recipientStatus,
                  recipientEmail,
                );
              }
            }}
            sx={{ mb: 2, ml: 1, minWidth: 280 }}
          />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "Email",
                    "Status",
                    "Attempts",
                    "Sent",
                    "Delivered",
                    "Last error",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: "left",
                        padding: 8,
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recipients.map((recipient) => (
                  <tr key={recipient.id}>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.email}{" "}
                      {recipient.opened_at != null && (
                        <VisibilityIcon fontSize="small" titleAccess="Opened" />
                      )}
                      {recipient.clicked_at != null && (
                        <TouchAppIcon fontSize="small" titleAccess="Clicked" />
                      )}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.status}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.attempts}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.sent_at
                        ? new Date(recipient.sent_at).toLocaleString()
                        : "—"}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.delivered_at
                        ? new Date(recipient.delivered_at).toLocaleString()
                        : "—"}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                      {recipient.last_error || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={recipientPage <= 1}
            onClick={() =>
              openRecipients(
                selectedCampaign,
                recipientPage - 1,
                recipientStatus,
                recipientEmail,
              )
            }
          >
            Previous
          </Button>
          <Typography>
            Page {recipientPage} of {recipientPages}
          </Typography>
          <Button
            disabled={recipientPage >= recipientPages}
            onClick={() =>
              openRecipients(
                selectedCampaign,
                recipientPage + 1,
                recipientStatus,
                recipientEmail,
              )
            }
          >
            Next
          </Button>
          <Button onClick={() => setRecipientsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {busy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(255,255,255,.35)",
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress />
        </div>
      )}
    </div>
  );
}
